import { useEffect } from 'react';

import { User } from 'entities';

import { Column, Collapse, Tag, Row, Dot } from 'components/Blocks';

import { ChevronDownIcon, ChevronUpIcon } from 'components/Icons';

import { ProjectOrTask } from '../ProjectOrTask';

import { useToggle, useUser } from 'hooks';
import { useUserEntries } from '../userEntries.hook';

import { getFirstAndLastName } from 'utils/text';
import { verifyHasExpiredDate, verifyIsExpiredDate } from '../utils';

interface TeamUserProps {
  children?: never;
  user: User;
  onlyDisplayEntries?: boolean;
  onOverdueChange: (hasOverdue: boolean) => void;
}

export function TeamUser({ user, onlyDisplayEntries, onOverdueChange }: TeamUserProps) {
  const { isOn, toggle } = useToggle();

  const loggedUser = useUser();

  const shouldDisplayProjects = loggedUser.team.canAssumeProject;

  const { projects, tasks, getStatusColor } = useUserEntries(user);

  const hasOverdue =
    verifyHasExpiredDate(projects) && shouldDisplayProjects
      ? projects.some(({ tasks }) => verifyHasExpiredDate(tasks))
      : false;

  useEffect(() => {
    onOverdueChange(hasOverdue);
    // its ok to sync like this!
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasOverdue]);

  const names = getFirstAndLastName(user.name).split(' ');

  const entries = shouldDisplayProjects ? projects : tasks;

  return (
    <Column gap="0">
      {!onlyDisplayEntries && (
        <button
          className="flex items-center gap-2 rounded-md px-2 py-1 hover:bg-hover"
          disabled={!entries.length}
          onClick={toggle}
        >
          {isOn ? <ChevronUpIcon /> : <ChevronDownIcon />}

          <Row className="w-[120px] overflow-hidden text-ellipsis whitespace-nowrap" gap="1">
            {names.map((name, index) => (
              <span
                key={`${name}--${index}`}
                className="relative break-words text-start text-xs font-semibold"
              >
                {name}
              </span>
            ))}
          </Row>

          <span className="relative ml-auto flex">
            <Tag
              name={entries.length}
              color="primary-foreground"
              className="w-8 text-primary dark:bg-muted dark:text-muted-foreground"
              size="tiny"
            />

            {hasOverdue && (
              <Dot
                size={6}
                color="destructive-dark"
                className="absolute -left-3 top-1/2 -translate-y-1/2"
              />
            )}
          </span>
        </button>
      )}

      <Collapse expanded={onlyDisplayEntries ? true : isOn} onlyRenderIfExpanded>
        <Column className="pt-3" gap="3">
          {entries.map((entry) => (
            <ProjectOrTask
              key={`${entry.id}-${user.id}-team-view`}
              shouldDisplayProjects={shouldDisplayProjects}
              getStatusColor={getStatusColor}
              entry={entry}
              addErrorIndicator={verifyIsExpiredDate(entry)}
            />
          ))}
        </Column>
      </Collapse>
    </Column>
  );
}
